import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { getUtm } from "@/lib/utm";
import { toast } from "sonner";
import { CreditCard, ShieldCheck } from "lucide-react";

const PLAN_LABEL = {
  starter: { name: "Starter", price: "$49/mo", trial: "7-day free trial", ctaHint: "We'll charge on day 7 — cancel anytime before to pay nothing.", cta: "Continue to secure checkout" },
  pro: { name: "Pro", price: "$149/mo", trial: "Billed today", ctaHint: "You'll be charged $149 today. Full Pro access unlocks instantly.", cta: "Continue to secure checkout" },
};

export default function Signup() {
  const [form, setForm] = useState({ name: "", email: "", password: "", business_name: "" });
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const planId = params.get("plan") === "pro" ? "pro" : "starter";
  const plan = PLAN_LABEL[planId];

  useEffect(() => {
    if (!params.get("plan")) {
      // Force plan choice first
      nav("/plans", { replace: true });
    }
  }, [params, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup({ ...form, plan: planId, utm: getUtm() || undefined });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
      setLoading(false);
      return;
    }
    // Account created — now try to start Stripe Checkout. If this fails, keep the user in the app
    // rather than leave them on a stuck loading screen; they can retry from /plans.
    try {
      const r = await api.post("/payments/checkout", {
        plan: planId,
        origin_url: window.location.origin,
      });
      if (r?.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
        return;
      }
      throw new Error("Missing checkout URL");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Account created, but we couldn't start checkout. Head to Plans to try again."
      );
      setLoading(false);
      nav("/plans", { replace: true });
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="signup-form">
          <Link to="/" className="flex items-center gap-2" data-testid="signup-brand-link">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-[#f9f8f6] font-serif-display text-xl" style={{ background: "#163325" }}>C</div>
            <span className="font-serif-display text-2xl">Coachly</span>
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <Badge className="capitalize" style={{ background: "#163325", color: "#f9f8f6", fontWeight: 500 }} data-testid="signup-plan-badge">{plan?.name}</Badge>
              <span className="text-xs text-[#5c5c5c]">{plan?.price} · {plan?.trial}</span>
            </div>
            <h1 className="font-serif-display text-4xl leading-none mt-3">Start your trial.</h1>
            <p className="text-[#5c5c5c] mt-2 text-sm">{plan?.ctaHint}</p>
            <Link to="/plans" className="text-xs text-[#163325] underline underline-offset-4 mt-2 inline-block" data-testid="signup-change-plan">Change plan</Link>
          </div>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#5c5c5c]">Your name</Label>
              <Input data-testid="signup-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-white border-[#e5e2da] focus-visible:ring-[#163325]" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#5c5c5c]">Coaching business</Label>
              <Input data-testid="signup-business-input" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="mt-1 bg-white border-[#e5e2da] focus-visible:ring-[#163325]" placeholder="Optional" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#5c5c5c]">Email</Label>
              <Input data-testid="signup-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 bg-white border-[#e5e2da] focus-visible:ring-[#163325]" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#5c5c5c]">Password</Label>
              <Input data-testid="signup-password-input" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 bg-white border-[#e5e2da] focus-visible:ring-[#163325]" />
            </div>
          </div>
          <Button type="submit" disabled={loading} data-testid="signup-submit-btn" className="w-full rounded-full bg-[#163325] hover:bg-[#0e2419] text-[#f9f8f6] h-11">
            <CreditCard className="w-4 h-4 mr-2" /> {loading ? "Redirecting to secure checkout…" : "Continue to secure checkout"}
          </Button>
          <div className="flex items-center gap-2 text-xs text-[#5c5c5c]">
            <ShieldCheck className="w-3.5 h-3.5 text-[#163325]" /> Payment secured by Stripe. {planId === "starter" ? "Card is only charged after your 7-day trial ends." : "Card is charged today; full Pro access unlocks instantly."}
          </div>
          <p className="text-sm text-[#5c5c5c]">
            Already have an account?{" "}
            <Link to="/login" className="text-[#163325] underline underline-offset-4" data-testid="signup-login-link">Sign in</Link>
          </p>
        </form>
      </div>
      <div className="hidden lg:block relative" style={{ background: "#163325" }}>
        <img
          src="https://images.unsplash.com/photo-1671716784499-a3d26826d844?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHdhcm0lMjBlYXJ0aHklMjBhZXN0aGV0aWMlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc4NTcxMzQ1OHww&ixlib=rb-4.1.0&q=85"
          alt="Warm textured background"
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 flex flex-col justify-end p-12 text-[#f9f8f6]">
          <p className="font-serif-display text-4xl leading-tight max-w-md">Stop losing warm leads because you replied too late.</p>
          <ul className="mt-6 space-y-2 text-sm opacity-90">
            <li>· 24/7 AI assistant on your site</li>
            <li>· 4-email nurture sequence baked in</li>
            <li>· Full lead + conversation dashboard</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
